import { Card } from "@/components/ui/card";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useEffect, useState, useRef } from "react";

interface Review {
  name: string;
  title: string;
  date: string;
  rating: number;
  quote: string;
  highlight?: string;
  industry?: string;
}

interface CaseStudiesProps {
  title?: string;
  subtitle?: string;
  reviews?: Review[];
}

// Animated counter hook
const useCountUp = (end: number, duration: number = 2000, isDecimal: boolean = false) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(isDecimal ? parseFloat((easeOut * end).toFixed(1)) : Math.floor(easeOut * end));
      
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [hasStarted, end, duration, isDecimal]);

  return { count, ref };
};

const AnimatedStat = ({ 
  value, 
  suffix = "", 
  label, 
  isDecimal = false 
}: { 
  value: number; 
  suffix?: string; 
  label: string; 
  isDecimal?: boolean;
}) => {
  const { count, ref } = useCountUp(value, 2000, isDecimal);

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
        {isDecimal ? count.toFixed(1) : count}{suffix}
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
};

const allReviews: Review[] = [
  {
    name: "Hasan Onuk",
    title: "Highly Recommend Vincent Growth Partners",
    date: "Sep 2, 2025",
    rating: 5,
    quote: "Working with Vincent Growth Partners has been a game-changer for our business. Their approach to SEO and lead generation is not just theory—it actually delivers measurable results. Within just a few months, we saw a significant increase in both the quality and quantity of leads coming in. What stood out most was their clear strategy and structured systems.",
    highlight: "Significant increase in lead quality and quantity",
    industry: "B2B Services",
  },
  {
    name: "Scott",
    title: "Excellent Guy and Very Knowledgeable",
    date: "Sep 5, 2025",
    rating: 5,
    quote: "Evan is an excellent guy and is very knowledgeable. Helped me scale and grow my local business. Would highly Recommend!",
    highlight: "Scaled and grew local business",
    industry: "Local Business",
  },
  {
    name: "Verified Client",
    title: "Tried and Tested Methods",
    date: "Jul 25, 2025",
    rating: 5,
    quote: "VGP has done an amazing job giving my business a full online exposure. We approached VGP with website development, and they not only built a great responsive website, but also helped us rank on top of the Google search in our area. This further boosted our presence from none at all to getting warm leads daily within a month.",
    highlight: "From zero to warm leads daily in 1 month",
    industry: "Local Services",
  },
  {
    name: "Oliver Woodhead",
    title: "Energy and Outbound Results",
    date: "Nov 24, 2025",
    rating: 5,
    quote: "We have a SAAS platform focussed on a Behavioural Psychometric Tool and our clients are sophisticated and focussed on working with great quality providers. Evan and the team have helped to refine our outbound campaigns and generate some great conversations. Evan approaches each challenge with passion and energy whilst looking for innovative approaches to drive performance.",
    highlight: "Refined outbound campaigns generating quality conversations",
    industry: "SaaS / B2B Tech",
  },
];

const CaseStudies = ({ 
  title = "Real Results from Real Clients",
  subtitle = "Don't just take our word for it. Here's what our clients say on Trustpilot.",
  reviews = allReviews 
}: CaseStudiesProps) => {
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-sm font-medium text-muted-foreground">Rated</span>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-[#00b67a] text-[#00b67a]" />
                ))}
              </div>
              <span className="text-sm font-medium text-muted-foreground">on</span>
              <span className="font-semibold text-foreground">Trustpilot</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>
          </div>

          {/* Animated Stats */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 mb-12 p-6 bg-background rounded-2xl border border-border/50">
            <AnimatedStat value={4} suffix="" label="Trustpilot Rating" isDecimal={true} />
            <AnimatedStat value={100} suffix="%" label="5-Star Reviews" />
            <AnimatedStat value={4} suffix="+" label="Verified Reviews" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {reviews.map((review, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300 border-border/50 bg-background">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary">
                        {review.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{review.name}</h4>
                      {review.industry && (
                        <span className="text-xs text-muted-foreground">{review.industry}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-[#00b67a] text-[#00b67a]" />
                    ))}
                  </div>
                </div>

                <h5 className="font-semibold text-foreground mb-3">{review.title}</h5>
                
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 h-6 w-6 text-primary/20" />
                  <p className="text-muted-foreground text-sm leading-relaxed pl-5">
                    {review.quote}
                  </p>
                </div>

                {review.highlight && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">Key Result:</span>
                      <span className="text-sm font-medium text-foreground">{review.highlight}</span>
                    </div>
                  </div>
                )}

                <div className="mt-3 text-xs text-muted-foreground">
                  {review.date} • Verified Trustpilot Review
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <a 
              href="https://www.trustpilot.com/review/vincentgrowthpartners.co.uk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              View all reviews on Trustpilot
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;

// Export specific review sets for different pages
export const demandGenReviews = [allReviews[0], allReviews[2]]; // Hasan & Verified Client (lead gen focused)
export const abmReviews = [allReviews[3], allReviews[0]]; // Oliver & Hasan (B2B/SaaS focused)
export const pipelineReviews = [allReviews[3], allReviews[2]]; // Oliver & Verified Client (outbound/results focused)
